
import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { KarmaAvailablePoints } from "../types/karma-types";

export const useKarmaClaimAvailability = () => {
  const [availablePoints, setAvailablePoints] = useState<KarmaAvailablePoints>({
    daily: false,
    weekly: false,
  });
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const { user } = useAuth();

  const checkAvailableKarmaPoints = useCallback(async () => {
    if (!user) {
      setAvailablePoints({ daily: false, weekly: false });
      setLoadingAvailability(false);
      return { daily: false, weekly: false };
    }

    setLoadingAvailability(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      oneWeekAgo.setHours(0, 0, 0, 0);

      const { data: recentClaimsData, error } = await supabase
        .from('karma_activities')
        .select('activity_type, created_at')
        .eq('user_id', user.id)
        .gte('created_at', oneWeekAgo.toISOString());

      if (error) {
        console.error("Error checking recent claims for availability:", error.message);
        setAvailablePoints({ daily: false, weekly: false }); // Default to false on error
        return { daily: false, weekly: false };
      }

      let dailyClaimed = false;
      let weeklyClaimed = false;

      if (recentClaimsData) {
        dailyClaimed = recentClaimsData.some(claim => {
          const claimDate = new Date(claim.created_at);
          claimDate.setHours(0, 0, 0, 0);
          return claim.activity_type === 'login' && claimDate.getTime() === today.getTime();
        });

        weeklyClaimed = recentClaimsData.some(claim =>
          claim.activity_type === 'activity' && new Date(claim.created_at) >= oneWeekAgo
        );
      }
      
      const currentAvailablePoints = { daily: !dailyClaimed, weekly: !weeklyClaimed };
      setAvailablePoints(currentAvailablePoints);
      return currentAvailablePoints;

    } catch (error: any) {
      console.error("Failed to check available karma points:", error.message);
      setAvailablePoints({ daily: false, weekly: false });
      return { daily: false, weekly: false };
    } finally {
      setLoadingAvailability(false);
    }
  }, [user]);

  return {
    availablePoints,
    setAvailablePoints,
    loadingAvailability,
    checkAvailableKarmaPoints,
  };
};
